/* eslint-disable @atlassian/relay/unused-fields */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	Fragment,
	useRef,
	type ReactNode,
	Suspense,
	useContext,
	useEffect,
	useMemo,
	useState,
	forwardRef,
	type ForwardRefExoticComponent,
	type RefAttributes,
} from 'react';

import { jsx } from '@compiled/react';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { CURRENT_SURFACE_CSS_VAR, useThemeObserver } from '@atlaskit/tokens';

import { EnvironmentContext } from '../../context';
import { parseExtensionId, useMemoizedStateObject } from '../../utils';

import { type CoreDataInner } from '@atlassian/forge-ui-types';

import stringify from 'fast-json-stable-stringify';

import { TracingProvider } from '../../error-reporting';
import {
	createSrcFromExtension,
	getBrowserLocale,
	getBrowserTimezone,
	getExtensionEntryPoint,
	getResolverContext,
	getSrcUrl,
	getViewContext,
	shouldUseCDNAuth,
	useCDNAuthSignedCookie,
} from './utils';
import type { HTMLCustomIFrameElement, IframeProps, ViewContext } from './types';
import { InnerIframe } from './InnerIframe';
import { getLoadingComponent } from './utils/getLoadingComponent';
import { TunnelsProvider } from './TunnelsProvider';
import { useForgeUiAnalyticsEvent } from '../../analytics/useForgeUiAnalyticsEvent';
import { getExtensionType } from '../../utils/getExtensionType';
import { ContextChangeObserverWrapper } from './ContextChangeObserverWrapper';
import { getEncodedAppContext } from './utils/getEncodedAppContext';
import { urlSafeB64 } from './utils/createSrcFromExtension';
import { fg } from '@atlaskit/platform-feature-flags';
import { InnerIframe as InnerIframeV2 } from './v2/InnerIframe';

const styles = cssMap({
	hostedResourcesIframeContainer: {
		position: 'relative',
	},
	box: {
		width: '100%',
		height: '100%',
	},
});

const ReloadOnContextChangeWrapper = ({
	context,
	children,
	getCurrentForgeReactMajorVersion,
}: {
	children: ReactNode;
	context: ViewContext;
	getCurrentForgeReactMajorVersion: IframeProps['getCurrentForgeReactMajorVersion'];
}) => {
	if (getCurrentForgeReactMajorVersion) {
		return (
			<ContextChangeObserverWrapper
				context={context}
				getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
			>
				{children}
			</ContextChangeObserverWrapper>
		);
	}
	return <Fragment key={stringify(context)}>{children}</Fragment>;
};

export const Iframe: ForwardRefExoticComponent<
	IframeProps & RefAttributes<HTMLCustomIFrameElement>
> = forwardRef<HTMLCustomIFrameElement, IframeProps>(
	(
		{
			accountId,
			extension,
			contextIds,
			apolloClient,
			components,
			coreData,
			extensionData,
			extensionViewData,
			loadingComponent,
			onLoad,
			height = '100%',
			width = '100%',
			bridge,
			customBridgeMethods,
			isResizable = true,
			entryPoint,
			extensionPayload,
			getContextToken,
			timezone,
			locale,
			isHidden = false,
			isInModal = false,
			setShouldShowRenderer,
			setIsLoading,
			getCurrentForgeReactMajorVersion,
			onError,
			isEmbeddedMacro = false,
			embeddedParentLocalId,
		},
		iframeRef,
	) => {
		const environment = useContext(EnvironmentContext);

		// ARKEN-856: useThemeObserver() is a bit noisy, it returns copies of the same theme which breaks useBridge() dependencies
		// We use useMemoizedStateObject() to properly memoize its output doing a deep-equality check
		const theme = useMemoizedStateObject(useThemeObserver());
		const iframeWrapperRef = useRef<HTMLDivElement>(null);

		const [surfaceColor, setSurfaceColor] = useState<string | null>(null);
		const { trackExtensionLoaded } = useForgeUiAnalyticsEvent();

		useEffect(() => {
			if (!iframeWrapperRef.current) {
				return;
			}
			const nextSurfaceColor = getComputedStyle(iframeWrapperRef.current).getPropertyValue(
				CURRENT_SURFACE_CSS_VAR,
			);

			setSurfaceColor(nextSurfaceColor);
		}, [theme]);

		const { appId, moduleKey } = parseExtensionId(extension.id);
		const { environmentId, environmentType } = extension;

		const extensionEntryPoint = getExtensionEntryPoint(extension, entryPoint);

		const urlSafeContext = urlSafeB64(getEncodedAppContext(extension, extensionViewData));

		const defaultSrc = useMemo(
			() => createSrcFromExtension(appId, extension, environment, extensionEntryPoint),
			[appId, extension, environment, extensionEntryPoint],
		);

		const isSignedCookieLoading = useCDNAuthSignedCookie();

		useEffect(() => {
			const renderType = extension?.properties?.render === 'native' ? 'UIKit' : 'CustomUI';
			const extensionType = getExtensionType(extension);
			trackExtensionLoaded({
				forgeEnvironment: extension?.environmentType,
				renderType,
				extensionType,
			});
			// This should run for each new unique appId/moduleKey combination
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [appId, moduleKey]);

		const [isThreeLOPromptVisible, setIsThreeLOPromptVisible] = useState(false);

		const coreDataInner: CoreDataInner = {
			...coreData,
			environmentId,
			environmentType,
			moduleKey,
			siteUrl: window.location.origin,
		};

		const resizingEnabled = isResizable && !extension.properties.viewportSize;

		// moves specific macro extension data (isEditing, macro) to extensionData instead of extensionPayload as this is required for Tempo (#hot-300905)
		// the first check enables the feature for Tempo specifically, the second check enables the feature for all extensions if needed
		const enabledForTempo =
			appId === '76206a70-b9d6-4328-9e20-db79196c548a' && fg('forge-ui-move-macro-data-for-tempo');
		const shouldIncludeMacroDataInExtensionData =
			enabledForTempo || fg('forge-ui-move-macro-data-to-extension-data');

		const loading = shouldUseCDNAuth() && isSignedCookieLoading;

		if (fg('forge-ui-refactor-iframe')) {
			return (
				<TunnelsProvider
					environmentType={extension.environmentType}
					appId={appId}
					contextIds={contextIds}
					environmentId={environmentId}
				>
					{({ tunnels, loading }) => (
						<Box
							ref={iframeWrapperRef}
							testId="hosted-resources-iframe-container"
							xcss={styles.hostedResourcesIframeContainer}
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
								minHeight:
									isThreeLOPromptVisible || isHidden || isEmbeddedMacro ? undefined : height,
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
								height: resizingEnabled || isThreeLOPromptVisible || isHidden ? undefined : height,
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
								width,
							}}
						>
							{(loading || (shouldUseCDNAuth() && isSignedCookieLoading)) && (
								<Suspense fallback={null}>
									<Box xcss={styles.box}>
										{/* This hides another loading spinner we have that is shown while we are fetching the list of current active tunnels.
										This is not needed for UI Kit 2 (where isHidden = true) since we'll already show a loading spinner with the RendererNext component */}
										{loading && !isHidden && getLoadingComponent(loadingComponent)}
									</Box>
								</Suspense>
							)}
							{!(loading || (shouldUseCDNAuth() && isSignedCookieLoading)) && (
								<TracingProvider>
									{fg('forge-ui-bridge-core-reset') ? (
										<InnerIframeV2
											accountId={accountId}
											extension={extension}
											contextIds={contextIds}
											apolloClient={apolloClient}
											components={components}
											coreData={coreDataInner}
											extensionData={extensionData}
											extensionViewData={extensionViewData}
											loadingComponent={loadingComponent}
											onLoad={onLoad}
											height={height}
											width={width}
											src={getSrcUrl(tunnels, extensionEntryPoint, defaultSrc, urlSafeContext)}
											bridge={bridge}
											customBridgeMethods={customBridgeMethods}
											isResizable={resizingEnabled}
											getContextToken={getContextToken}
											timezone={timezone}
											locale={locale}
											setIsThreeLOPromptVisible={setIsThreeLOPromptVisible}
											setShouldShowRenderer={setShouldShowRenderer}
											extensionPayload={extensionPayload}
											isHidden={isHidden}
											theme={theme}
											surfaceColor={surfaceColor}
											appId={appId}
											ref={iframeRef}
											isInModal={isInModal}
											setIsLoading={setIsLoading}
											onError={onError}
											isEmbeddedMacro={isEmbeddedMacro}
											embeddedParentLocalId={embeddedParentLocalId}
										/>
									) : (
										<ReloadOnContextChangeWrapper
											getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
											context={getViewContext({
												resolverContext: getResolverContext(coreDataInner, extensionData),
												accountId,
												extension,
												timezone: timezone ?? getBrowserTimezone(),
												locale: locale ?? getBrowserLocale(),
												extensionViewData,
												extensionPayload: {
													config: extensionPayload?.config,
													macro: shouldIncludeMacroDataInExtensionData
														? extensionData?.macro
														: extensionPayload?.macro,
													isEditing: shouldIncludeMacroDataInExtensionData
														? extensionData?.isEditing
														: extensionPayload?.isEditing,
												},
											})}
										>
											<InnerIframe
												accountId={accountId}
												extension={extension}
												contextIds={contextIds}
												apolloClient={apolloClient}
												components={components}
												coreData={coreDataInner}
												extensionData={extensionData}
												extensionViewData={extensionViewData}
												loadingComponent={loadingComponent}
												onLoad={onLoad}
												height={height}
												width={width}
												src={getSrcUrl(tunnels, extensionEntryPoint, defaultSrc, urlSafeContext)}
												bridge={bridge}
												customBridgeMethods={customBridgeMethods}
												isResizable={resizingEnabled}
												getContextToken={getContextToken}
												timezone={timezone}
												locale={locale}
												setIsThreeLOPromptVisible={setIsThreeLOPromptVisible}
												setShouldShowRenderer={setShouldShowRenderer}
												extensionPayload={extensionPayload}
												isHidden={isHidden}
												theme={theme}
												surfaceColor={surfaceColor}
												appId={appId}
												ref={iframeRef}
												isInModal={isInModal}
												setIsLoading={setIsLoading}
												onError={onError}
												isEmbeddedMacro={isEmbeddedMacro}
												embeddedParentLocalId={embeddedParentLocalId}
											/>
										</ReloadOnContextChangeWrapper>
									)}
								</TracingProvider>
							)}
						</Box>
					)}
				</TunnelsProvider>
			);
		}

		// TODO: To remove once forge-ui-refactor-iframe is fully rolled out
		if (extension.environmentType === 'DEVELOPMENT') {
			return (
				<TunnelsProvider
					environmentType="DEVELOPMENT"
					appId={parseExtensionId(extension.id).appId}
					contextIds={contextIds}
					environmentId={extension.environmentId}
				>
					{({ tunnels: relayTunnels, loading: isTunnelsLoading }) => {
						const combinedLoading =
							isTunnelsLoading || (shouldUseCDNAuth() && isSignedCookieLoading);

						return (
							<Box
								ref={iframeWrapperRef}
								testId="hosted-resources-iframe-container"
								xcss={styles.hostedResourcesIframeContainer}
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
									minHeight:
										isThreeLOPromptVisible || isHidden || isEmbeddedMacro ? undefined : height,
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
									height:
										resizingEnabled || isThreeLOPromptVisible || isHidden ? undefined : height,
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
									width,
								}}
							>
								{combinedLoading && (
									<Suspense fallback={null}>
										<Box xcss={styles.box}>
											{combinedLoading && !isHidden && getLoadingComponent(loadingComponent)}
										</Box>
									</Suspense>
								)}
								{!combinedLoading && (
									<TracingProvider>
										<ReloadOnContextChangeWrapper
											getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
											context={getViewContext({
												resolverContext: getResolverContext(coreDataInner, extensionData),
												accountId,
												extension,
												timezone: timezone ?? getBrowserTimezone(),
												locale: locale ?? getBrowserLocale(),
												extensionViewData,
												extensionPayload: {
													config: extensionPayload?.config,
													macro: shouldIncludeMacroDataInExtensionData
														? extensionData?.macro
														: extensionPayload?.macro,
													isEditing: shouldIncludeMacroDataInExtensionData
														? extensionData?.isEditing
														: extensionPayload?.isEditing,
												},
											})}
										>
											<InnerIframe
												accountId={accountId}
												extension={extension}
												contextIds={contextIds}
												components={components}
												coreData={coreDataInner}
												extensionData={extensionData}
												extensionViewData={extensionViewData}
												loadingComponent={loadingComponent}
												onLoad={onLoad}
												height={height}
												width={width}
												src={getSrcUrl(
													relayTunnels,
													extensionEntryPoint,
													defaultSrc,
													urlSafeContext,
												)}
												bridge={bridge}
												customBridgeMethods={customBridgeMethods}
												isResizable={resizingEnabled}
												getContextToken={getContextToken}
												timezone={timezone}
												locale={locale}
												setIsThreeLOPromptVisible={setIsThreeLOPromptVisible}
												setShouldShowRenderer={setShouldShowRenderer}
												extensionPayload={extensionPayload}
												isHidden={isHidden}
												theme={theme}
												surfaceColor={surfaceColor}
												appId={appId}
												ref={iframeRef}
												isInModal={isInModal}
												setIsLoading={setIsLoading}
												onError={onError}
												isEmbeddedMacro={isEmbeddedMacro}
												embeddedParentLocalId={embeddedParentLocalId}
											/>
										</ReloadOnContextChangeWrapper>
									</TracingProvider>
								)}
							</Box>
						);
					}}
				</TunnelsProvider>
			);
		}

		// TODO: To remove once forge-ui-refactor-iframe is fully rolled out
		return (
			<Box
				ref={iframeWrapperRef}
				testId="hosted-resources-iframe-container"
				xcss={styles.hostedResourcesIframeContainer}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					minHeight: isThreeLOPromptVisible || isHidden || isEmbeddedMacro ? undefined : height,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					height: resizingEnabled || isThreeLOPromptVisible || isHidden ? undefined : height,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					width,
				}}
			>
				{loading && (
					<Suspense fallback={null}>
						<Box xcss={styles.box}>
							{/* This hides another loading spinner we have that is shown while we are fetching the list of current active tunnels.
              				This is not needed for UI Kit 2 (where isHidden = true) since we'll already show a loading spinner with the RendererNext component */}
							{loading && !isHidden && getLoadingComponent(loadingComponent)}
						</Box>
					</Suspense>
				)}
				{!loading && (
					<TracingProvider>
						<ReloadOnContextChangeWrapper
							getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
							context={getViewContext({
								resolverContext: getResolverContext(coreDataInner, extensionData),
								accountId,
								extension,
								timezone: timezone ?? getBrowserTimezone(),
								locale: locale ?? getBrowserLocale(),
								extensionViewData,
								extensionPayload: {
									config: extensionPayload?.config,
									macro: shouldIncludeMacroDataInExtensionData
										? extensionData?.macro
										: extensionPayload?.macro,
									isEditing: shouldIncludeMacroDataInExtensionData
										? extensionData?.isEditing
										: extensionPayload?.isEditing,
								},
							})}
						>
							<InnerIframe
								accountId={accountId}
								extension={extension}
								contextIds={contextIds}
								components={components}
								coreData={coreDataInner}
								extensionData={extensionData}
								extensionViewData={extensionViewData}
								loadingComponent={loadingComponent}
								onLoad={onLoad}
								height={height}
								width={width}
								src={getSrcUrl([], extensionEntryPoint, defaultSrc, urlSafeContext)}
								bridge={bridge}
								customBridgeMethods={customBridgeMethods}
								isResizable={resizingEnabled}
								getContextToken={getContextToken}
								timezone={timezone}
								locale={locale}
								setIsThreeLOPromptVisible={setIsThreeLOPromptVisible}
								setShouldShowRenderer={setShouldShowRenderer}
								extensionPayload={extensionPayload}
								isHidden={isHidden}
								theme={theme}
								surfaceColor={surfaceColor}
								appId={appId}
								ref={iframeRef}
								isInModal={isInModal}
								setIsLoading={setIsLoading}
								onError={onError}
								isEmbeddedMacro={isEmbeddedMacro}
								embeddedParentLocalId={embeddedParentLocalId}
							/>
						</ReloadOnContextChangeWrapper>
					</TracingProvider>
				)}
			</Box>
		);
	},
);
